import { browser } from '$app/environment';
import { Capacitor } from '@capacitor/core';
import { readSettings } from './settings';

let ctx: AudioContext | null = null;
const audioQueue: Array<() => void> = [];
let hasGesture = false;

let gestureResolve: (() => void) | null = null;
const gesturePromise: Promise<void> = browser
	? new Promise(r => { gestureResolve = r; })
	: Promise.resolve();

if (browser) {
	document.addEventListener('pointerup', () => {
		hasGesture = true;
		if (!ctx) ctx = new AudioContext();
		ctx.resume().then(() => {
			gestureResolve?.();
			gestureResolve = null;
			audioQueue.splice(0).forEach(fn => fn());
		});
	}, { once: true, capture: true });
}

export function unlockAudio(): Promise<void> {
	return gesturePromise;
}

function getCtx(): AudioContext | null {
	if (!ctx) return null;
	return ctx;
}

function tone(
	freq: number,
	type: OscillatorType,
	duration: number,
	gain = 0.2,
	offset = 0,
	freqEnd?: number
) {
	const c = getCtx();
	if (!c) return;

	if (c.state === 'suspended') {
		audioQueue.push(() => tone(freq, type, duration, gain, offset, freqEnd));
		return;
	}

	const osc = c.createOscillator();
	const env = c.createGain();
	osc.connect(env);
	env.connect(c.destination);

	const t = c.currentTime + offset;
	osc.type = type;
	osc.frequency.setValueAtTime(freq, t);
	if (freqEnd !== undefined) osc.frequency.linearRampToValueAtTime(freqEnd, t + duration);

	const vol = readSettings().soundVolume;
	env.gain.setValueAtTime(0, t);
	env.gain.linearRampToValueAtTime(gain * vol, t + 0.005);
	env.gain.exponentialRampToValueAtTime(0.001, t + duration);

	osc.start(t);
	osc.stop(t + duration);
}

function enabled() {
	return readSettings().soundEnabled;
}

function hapticsEnabled() {
	return readSettings().hapticsEnabled;
}

const isNativeAndroid = browser && Capacitor.getPlatform() === 'android';

// ImpactStyle and NotificationType enum values from @capacitor/haptics
type ImpactStyleValue = 'HEAVY' | 'MEDIUM' | 'LIGHT';
type NotificationTypeValue = 'SUCCESS' | 'WARNING' | 'ERROR';

async function hapticImpact(style: ImpactStyleValue) {
	if (!browser || !hapticsEnabled()) return;
	if (isNativeAndroid) {
		try {
			const { Haptics } = await import('@capacitor/haptics');
			await Haptics.impact({ style: style as never });
		} catch {}
	} else if (hasGesture && 'vibrate' in navigator) {
		navigator.vibrate(style === 'HEAVY' ? 60 : style === 'MEDIUM' ? 40 : 20);
	}
}

async function hapticNotification(type: NotificationTypeValue) {
	if (!browser || !hapticsEnabled()) return;
	if (isNativeAndroid) {
		try {
			const { Haptics } = await import('@capacitor/haptics');
			await Haptics.notification({ type: type as never });
		} catch {}
	} else if (hasGesture && 'vibrate' in navigator) {
		navigator.vibrate(type === 'SUCCESS' ? [40, 30, 40, 30, 80] : [30, 20, 30]);
	}
}

export const sound = {
	hover() {
		if (!enabled()) return;
		tone(600, 'sine', 0.08, 0.08, 0, 200);
	},

	tap(haptic = true) {
		if (haptic) hapticImpact('LIGHT');
		if (!enabled()) return;
		tone(330, 'square', 0.05, 0.12);
	},

	key() {
		hapticImpact('LIGHT');
		if (!enabled()) return;
		tone(440, 'sine', 0.03, 0.04);
	},

	correct() {
		hapticImpact('MEDIUM');
		if (!enabled()) return;
		tone(523, 'square', 0.08, 0.18);
		tone(659, 'square', 0.08, 0.18, 0.09);
	},

	wrong() {
		hapticNotification('ERROR');
		if (!enabled()) return;
		tone(220, 'square', 0.12, 0.18, 0, 160);
	},

	connect(step = 0) {
		hapticImpact('LIGHT');
		if (!enabled()) return;
		const freq = 480 + step * 35;
		tone(freq, 'sine', 0.07, 0.14);
	},

	win() {
		hapticNotification('SUCCESS');
		if (!enabled()) return;
		const notes = [523, 659, 784, 1047];
		notes.forEach((freq, i) => tone(freq, 'square', 0.1, 0.2, i * 0.1));
	},

	countdownTick() {
		if (!enabled()) return;
		tone(660, 'square', 0.06, 0.16);
	},

	countdownGo() {
		hapticImpact('HEAVY');
		if (!enabled()) return;
		const notes = [784, 988, 1319];
		notes.forEach((freq, i) => tone(freq, 'square', 0.12, 0.22, i * 0.06));
	},

	friendRequest() {
		hapticNotification('WARNING');
		if (!enabled()) return;
		tone(660, 'sine', 0.12, 0.15);
		tone(880, 'sine', 0.14, 0.15, 0.1);
	},

	friendAccepted() {
		hapticNotification('SUCCESS');
		if (!enabled()) return;
		tone(523, 'sine', 0.09, 0.16);
		tone(659, 'sine', 0.09, 0.16, 0.08);
		tone(784, 'sine', 0.11, 0.16, 0.16);
	},

	gameInvite() {
		hapticNotification('WARNING');
		if (!enabled()) return;
		tone(523, 'square', 0.10, 0.18);
		tone(659, 'square', 0.10, 0.18, 0.09);
		tone(784, 'square', 0.10, 0.20, 0.18);
		tone(1047, 'square', 0.12, 0.22, 0.27);
	},

	playerJoined() {
		hapticNotification('SUCCESS');
		if (!enabled()) return;
		tone(659, 'square', 0.08, 0.18);
		tone(880, 'square', 0.10, 0.20, 0.09);
	},

	playerLeft() {
		hapticNotification('ERROR');
		if (!enabled()) return;
		tone(440, 'sine', 0.12, 0.16, 0, 220);
	},

	// Yellow "right letter, wrong spot" — softer/warmer than correct, so it
	// reads as partial progress rather than a win.
	present() {
		hapticImpact('LIGHT');
		if (!enabled()) return;
		tone(392, 'sine', 0.09, 0.16, 0, 440);
	},

	// Illegal input (not in word list, bad move) — a gentle low blip, distinct
	// from `wrong` which is reserved for an actual scored mistake.
	invalid() {
		hapticNotification('WARNING');
		if (!enabled()) return;
		tone(240, 'sine', 0.1, 0.14, 0, 196);
	},

	// Hint used — a quiet ascending shimmer (reveal feel).
	hint() {
		hapticImpact('LIGHT');
		if (!enabled()) return;
		tone(660, 'sine', 0.07, 0.1);
		tone(990, 'sine', 0.09, 0.1, 0.07);
	},

	// Counterpart to `win` — a short descending phrase for a loss / out of guesses.
	lose() {
		hapticNotification('ERROR');
		if (!enabled()) return;
		const notes = [392, 330, 262];
		notes.forEach((freq, i) => tone(freq, 'square', 0.12, 0.2, i * 0.11));
	},

	// Per-tile resolve tick as a guess row flips open (call with the tile
	// index so the pitch steps up across the row, like `connect`).
	reveal(step = 0) {
		if (!enabled()) return;
		tone(320 + step * 40, 'triangle', 0.05, 0.1);
	},

	// Word Group — a solved group. Pass the group index (0-3) so each
	// successive group resolves a step higher / brighter.
	groupSolved(step = 0) {
		hapticNotification('SUCCESS');
		if (!enabled()) return;
		const root = 523 + step * 49;
		tone(root, 'square', 0.09, 0.18);
		tone(root * 1.25, 'square', 0.09, 0.18, 0.08);
		tone(root * 1.5, 'square', 0.11, 0.2, 0.16);
	},

	// Word Find — a regular theme word located.
	wordFound() {
		hapticImpact('MEDIUM');
		if (!enabled()) return;
		tone(587, 'sine', 0.08, 0.18);
		tone(880, 'sine', 0.1, 0.2, 0.07);
	},

	// Word Find — the spangram / anchor word. Bigger, brighter than wordFound.
	spangram() {
		hapticNotification('SUCCESS');
		if (!enabled()) return;
		const notes = [659, 880, 1175, 1568];
		notes.forEach((freq, i) => tone(freq, 'sine', 0.11, 0.22, i * 0.08));
	},

	// Word Build — pangram (uses all 7 letters). Celebratory, above `correct`.
	pangram() {
		hapticNotification('SUCCESS');
		if (!enabled()) return;
		const notes = [523, 659, 784, 1047, 1319];
		notes.forEach((freq, i) => tone(freq, 'square', 0.1, 0.22, i * 0.07));
	},

	// Rank promotion (Word Build ranks). Rising triad, lighter than `win`.
	rankUp() {
		hapticImpact('MEDIUM');
		if (!enabled()) return;
		const notes = [659, 831, 988];
		notes.forEach((freq, i) => tone(freq, 'sine', 0.1, 0.18, i * 0.09));
	},

	// Hitting a streak milestone (5/10/… day streak). Triumphant flourish.
	streakMilestone() {
		hapticNotification('SUCCESS');
		if (!enabled()) return;
		const notes = [523, 784, 1047, 784, 1319];
		notes.forEach((freq, i) => tone(freq, 'square', 0.1, 0.22, i * 0.09));
	},

	// A player marked ready in the coop lobby — short affirmative up-blip.
	ready() {
		hapticImpact('LIGHT');
		if (!enabled()) return;
		tone(660, 'sine', 0.07, 0.16);
		tone(880, 'sine', 0.08, 0.16, 0.06);
	},

	// A player cleared ready — soft down-blip (mirror of `ready`).
	unready() {
		hapticImpact('LIGHT');
		if (!enabled()) return;
		tone(660, 'sine', 0.07, 0.14);
		tone(440, 'sine', 0.09, 0.14, 0.06);
	},

	// Coop: it just became this player's turn — a clear attention two-tone.
	yourTurn() {
		hapticNotification('WARNING');
		if (!enabled()) return;
		tone(784, 'sine', 0.1, 0.18);
		tone(1047, 'sine', 0.12, 0.2, 0.09);
	},

	// Very subtle tick when a toast appears (kept quiet so it layers under any
	// event sound that fired alongside it).
	toast() {
		if (!enabled()) return;
		tone(740, 'sine', 0.04, 0.05);
	},

	// A fresh daily / puzzles reset — bright ascending chime.
	newDaily() {
		hapticImpact('MEDIUM');
		if (!enabled()) return;
		const notes = [523, 659, 784, 1047];
		notes.forEach((freq, i) => tone(freq, 'sine', 0.1, 0.18, i * 0.08));
	},

	// Game added to favorites — bright affirmative two-note up.
	favoriteAdd() {
		hapticNotification('SUCCESS');
		if (!enabled()) return;
		tone(659, 'sine', 0.08, 0.18);
		tone(988, 'sine', 0.1, 0.2, 0.07);
	},

	// Game removed from favorites — soft descending two-note.
	favoriteRemove() {
		hapticImpact('LIGHT');
		if (!enabled()) return;
		tone(659, 'sine', 0.08, 0.14);
		tone(440, 'sine', 0.09, 0.14, 0.07);
	},

	// Reorder: a card shifted to a new slot while organizing — short tick.
	reorderMove() {
		hapticImpact('LIGHT');
		if (!enabled()) return;
		tone(520, 'triangle', 0.04, 0.1);
	},

	// Reorder: the dragged card settled into its final slot.
	reorderDrop() {
		hapticImpact('MEDIUM');
		if (!enabled()) return;
		tone(392, 'square', 0.06, 0.16);
		tone(523, 'square', 0.07, 0.16, 0.05);
	}
};

export function sfx(node: HTMLElement) {
	const SLOP = 10;
	let cancelled = false;
	let startX = 0;
	let startY = 0;

	function ondown(e: PointerEvent) {
		cancelled = false;
		startX = e.clientX;
		startY = e.clientY;
		sound.tap(false); // sound immediately, haptic deferred to pointerup
	}
	function onmove(e: PointerEvent) {
		if (cancelled) return;
		const dx = e.clientX - startX;
		const dy = e.clientY - startY;
		if (dx * dx + dy * dy > SLOP * SLOP) cancelled = true;
	}
	function oncancel() {
		cancelled = true; // browser took over for scroll
	}
	function onup() {
		if (!cancelled) hapticImpact('LIGHT');
	}

	node.addEventListener('pointerdown', ondown);
	node.addEventListener('pointermove', onmove);
	node.addEventListener('pointercancel', oncancel);
	node.addEventListener('pointerup', onup);
	return {
		destroy() {
			node.removeEventListener('pointerdown', ondown);
			node.removeEventListener('pointermove', onmove);
			node.removeEventListener('pointercancel', oncancel);
			node.removeEventListener('pointerup', onup);
		}
	};
}
